// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 17.9.0 — Floating tour controller overlay bar.
 * Shows play/pause, speed, progress, mode toggle, and exit button.
 */

import { createPortal } from 'react-dom';
import type { TourMode, TourSpeed, TourEngineStatus } from '../hooks/useTourEngine.js';
import { useLocale } from '../hooks/useLocale.js';

interface TourControllerProps {
  status: TourEngineStatus;
  currentStep: number;
  totalSteps: number;
  mode: TourMode;
  speed: TourSpeed;
  tourTitle: string;
  onPlayPause: () => void;
  onNext: () => void;
  onPrev: () => void;
  onSpeedChange: (speed: TourSpeed) => void;
  onModeChange: (mode: TourMode) => void;
  onExit: () => void;
}

const SPEEDS: TourSpeed[] = [0.5, 1, 2];

export function TourController({
  status,
  currentStep,
  totalSteps,
  mode,
  speed,
  tourTitle,
  onPlayPause,
  onNext,
  onPrev,
  onSpeedChange,
  onModeChange,
  onExit,
}: TourControllerProps) {
  const { t } = useLocale();
  const isPaused = status === 'paused';
  const progressPct = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

  // Render into document.body via portal so the controller escapes any
  // ancestor stacking contexts and truly sits above driver.js's overlay.
  return createPortal(
    <div className="grc-tour-controller" role="toolbar" aria-label="Tour controls">
      {/* Progress bar */}
      <div className="grc-tour-controller__progress">
        <div className="grc-tour-controller__progress-fill" style={{ width: `${progressPct}%` }} />
      </div>

      <div className="grc-tour-controller__body">
        {/* Tour title */}
        <span className="grc-tour-controller__title">{tourTitle}</span>

        {/* Step counter */}
        <span className="grc-tour-controller__step-count">
          {t.onboarding.tours.step_of
            .replace('{current}', String(currentStep + 1))
            .replace('{total}', String(totalSteps))}
        </span>

        {/* Navigation buttons */}
        <div className="grc-tour-controller__nav">
          <button
            className="grc-tour-controller__btn"
            onClick={onPrev}
            disabled={currentStep <= 0}
            title={t.common.previous || 'Previous'}
            aria-label="Previous step"
          >
            ‹
          </button>

          {mode === 'autoplay' && (
            <button
              className="grc-tour-controller__btn grc-tour-controller__btn--play"
              onClick={onPlayPause}
              title={isPaused ? t.common.resume || 'Resume' : t.common.pause || 'Pause'}
              aria-label={isPaused ? 'Resume tour' : 'Pause tour'}
            >
              {isPaused ? '▶' : '⏸'}
            </button>
          )}

          <button
            className="grc-tour-controller__btn"
            onClick={onNext}
            disabled={mode === 'autoplay' && currentStep >= totalSteps - 1}
            title={t.onboarding.tours.next}
            aria-label="Next step"
          >
            ›
          </button>
        </div>

        {/* Speed selector (auto-play only) */}
        {mode === 'autoplay' && (
          <div className="grc-tour-controller__speed">
            {SPEEDS.map((s) => (
              <button
                key={s}
                className={`grc-tour-controller__speed-btn ${speed === s ? 'grc-tour-controller__speed-btn--active' : ''}`}
                onClick={() => onSpeedChange(s)}
                aria-label={`Speed ${s}x`}
              >
                {s}x
              </button>
            ))}
          </div>
        )}

        {/* Mode toggle */}
        <button
          className="grc-tour-controller__btn grc-tour-controller__btn--mode"
          onClick={() => onModeChange(mode === 'autoplay' ? 'interactive' : 'autoplay')}
          title={mode === 'autoplay' ? 'Switch to Interactive' : 'Switch to Auto-play'}
          aria-label="Toggle tour mode"
        >
          {mode === 'autoplay' ? '⚡' : '👆'}
          <span className="grc-tour-controller__mode-label">
            {mode === 'autoplay'
              ? t.onboarding?.tours?.autoplay || 'Auto-play'
              : t.onboarding?.tours?.interactive || 'Interactive'}
          </span>
        </button>

        {/* Exit button */}
        <button
          className="grc-tour-controller__btn grc-tour-controller__btn--exit"
          onClick={onExit}
          title={t.onboarding.tours.exit}
          aria-label="Exit tour"
        >
          ✕
        </button>
      </div>
    </div>,
    document.body,
  );
}
